import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Button, Col, Row, Card, CardBody, CardHeader, Spinner,
    Nav, NavItem, NavLink, TabContent, TabPane, Badge, Alert
} from "reactstrap";
import JSZip from "jszip";
import Select from "react-select";
import axios from "axios";
import { baseurl } from "../../../baseurl";
import { companyId, headers, username, token } from "../../../utils/variables";
import Swal from "sweetalert2";
import imageCompression from "browser-image-compression";
import Responseloader from "../../Forms/ShortCutComponents/ResponseLoader";
import { checkPermission } from "../../../Helper";

const BulkImageUploader = () => {
    const [zipFile, setZipFile] = useState(null);
    const [extractedImages, setExtractedImages] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [itemsList, setItemsList] = useState([]);
    const [isLoadingItems, setIsLoadingItems] = useState(true);
    const [isProcessingZip, setIsProcessingZip] = useState(false);
    const [activeTab, setActiveTab] = useState("matched");
    const [percentageLoader, setpercentageLoader] = useState(false);


    useEffect(() => {
        const funCall = async () => {
            await asyncRollCheck(0);
        }
        // funCall()
        fetchItems();
    }, []);

    const asyncRollCheck = async (value) => {
        let pathname = "/ItemImageUploader";
        let status = value;

        let hasPermission = await checkPermission(pathname, status);
        if (hasPermission) {
            return true;
        }
    };

    const fetchItems = async () => {
        try {
            const res = await axios.post(
                `${baseurl}/lovs/getItem`,
                { companyId, username, },
                headers
            );
            setItemsList(res.data.data || []);
        } catch (err) {
            console.error("Failed to load items for matching", err);
        } finally {
            setIsLoadingItems(false);
        }
    };

    const handleZipUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setZipFile(file);
        setIsProcessingZip(true);
        const zip = new JSZip();
        try {
            const unzipped = await zip.loadAsync(file);

            const imageFiles = Object.keys(unzipped.files).filter(filename => {
                const fileData = unzipped.files[filename];
                return !fileData.dir && filename.match(/\.(jpg|jpeg|png|webp|gif)$/i);
            });

            if (imageFiles.length === 0) {
                return Swal.fire("Warning", "No images found in the ZIP.", "warning");
            }

            if (imageFiles.length > 50) {
                setIsProcessingZip(false);
                return Swal.fire(
                    "Limit Exceeded",
                    `You can upload a maximum of 50 images at a time. Your ZIP contains ${imageFiles.length} images.`,
                    "warning"
                );
            }

            const options = {
                maxSizeMB: 0.3,
                maxWidthOrHeight: 1024,
                useWebWorker: true,
            };

            const imagesTemp = await Promise.all(
                imageFiles.map(async (filename) => {
                    const fileData = unzipped.files[filename];
                    const blob = await fileData.async("blob");
                    let finalFile = blob
                    try {
                        finalFile = await imageCompression(blob, options);
                    } catch (err) {
                        console.warn("Compression failed, using original:", filename);
                        finalFile = blob;
                    }
                    const url = URL.createObjectURL(finalFile);
                    const baseFilename = filename.split("/").pop();

                    return {
                        originalFilename: filename,
                        filename: baseFilename,
                        file: finalFile,
                        url,
                        selectedItems: []
                    };
                })
            );

            const filenamesToMatch = imageFiles;

            if (filenamesToMatch.length === 0) {
                return Swal.fire("Warning", "No images found in the ZIP.", "warning");
            }

            const matchRes = await axios.post(`${baseurl}/contaboImages/getItemImageNames`, {
                filenames: filenamesToMatch,
                companyId,
                username
            }, {
                headers: { authorization: token }
            });

            const backendMatches = matchRes.data.matches || [];

            const finalImagesWithMatches = imagesTemp.map(img => {
                const matchData = backendMatches.find(m => m.filename === img.originalFilename);
                return {
                    ...img,
                    selectedItems: matchData && matchData.matchedItem ? [matchData.matchedItem] : []
                };
            });

            setExtractedImages(finalImagesWithMatches);

            const hasUnmatched = finalImagesWithMatches.some(img => img.selectedItems.length === 0);
            setActiveTab(hasUnmatched ? "unmatched" : "matched");

        } catch (error) {
            console.error("Error unzipping or matching files:", error);
            Swal.fire("Error", "Failed to process the ZIP file.", "error");
        } finally {
            setIsProcessingZip(false);
        }
    };

    const handleItemSelect = (filename, selectedOptions) => {
        const newImages = extractedImages.map(img => {
            if (img.filename === filename) {
                return { ...img, selectedItems: selectedOptions || [] };
            }
            return img;
        });
        setExtractedImages(newImages);
    };

    const handleSave = async () => {
        setIsUploading(true);
        try {
            const duplicateCheck = await axios.post(
                `${baseurl}/contaboImages/checkFileHistory`,
                {
                    companyId,
                    username,
                    filename: zipFile.name,
                },
                { headers: { authorization: token } }
            );
            if (duplicateCheck.data.duplicate) {
                setIsUploading(false);
                return Swal.fire(
                    "Duplicate File",
                    duplicateCheck.data.message,
                    "error"
                );
            }
        } catch (err) {
            setIsUploading(false);
            console.error("Duplicate check failed:", err);
            return Swal.fire("Error", "Could not verify file history.", "error");
        }

        let mappedImages = extractedImages.filter(img => img.selectedItems && img.selectedItems.length > 0);

        if (mappedImages.length === 0) {
            setIsUploading(false);
            return Swal.fire("Hold on!", "Please map at least one image to an item before saving.", "warning");
        }
        try {
            const filenamesToCheck = mappedImages.map(img => img.filename);
            const imageDuplicateCheck = await axios.post(
                `${baseurl}/contaboImages/checkDuplicateImages`,
                {
                    companyId,
                    username,
                    filenames: filenamesToCheck
                },
                { headers: { authorization: token } }
            );

            const duplicateImages = imageDuplicateCheck.data.duplicates || [];

            if (duplicateImages.length > 0) {
                setIsUploading(false);
                const result = await Swal.fire({
                    title: 'Duplicate Images Found',
                    text: `${duplicateImages.length} of these images have already been uploaded previously. Do you want to skip the duplicates and upload the new ones?`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Skip Duplicates & Upload',
                    cancelButtonText: 'Cancel Upload'
                });

                if (!result.isConfirmed) {
                    return;
                }
                setIsUploading(true);
                mappedImages = mappedImages.filter(img => !duplicateImages.includes(img.filename));

                if (mappedImages.length === 0) {
                    setIsUploading(false);
                    return Swal.fire("Notice", "All mapped images were duplicates. Nothing new to upload.", "info");
                }
            }
        } catch (err) {
            setIsUploading(false);
            console.error("Image duplicate check failed:", err);
            return Swal.fire("Error", "Could not verify image duplication history.", "error");
        }
        setpercentageLoader(true);

        const BATCH_SIZE = 2;
        let successCount = 0;
        const totalImages = mappedImages.length;
        let successfullyUploadedFiles = [];
        try {
            for (let i = 0; i < totalImages; i += BATCH_SIZE) {
                const batch = mappedImages.slice(i, i + BATCH_SIZE);
                const formData = new FormData();
                const mappings = [];

                batch.forEach((img) => {
                    formData.append("images", img.file, img.filename);
                    mappings.push({
                        filename: img.filename,
                        itemIds: img.selectedItems.map(opt => opt.value)
                    });
                });

                formData.append("mappings", JSON.stringify(mappings));
                formData.append("companyId", companyId);
                formData.append("username", username);

                await axios.post(`${baseurl}/contaboImages/bulkUploadItems`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        authorization: token
                    }
                });

                successCount += batch.length;
                batch.forEach(img => successfullyUploadedFiles.push(img.filename));
            }

            try {
                await axios.post(
                    `${baseurl}/contaboImages/saveFileHistory`,
                    {
                        companyId,
                        username,
                        filename: zipFile.name,
                    },
                    { headers: { authorization: token } }
                );
            } catch (historyErr) {
                console.warn("File history save failed (upload was successful):", historyErr);
            }

            Swal.fire("Success!", `Successfully uploaded and linked ${successCount} images.`, "success");
            setExtractedImages(prev => prev.filter(img => !successfullyUploadedFiles.includes(img.filename)));

        } catch (error) {
            console.error("Upload failed", error);
            Swal.fire(
                "Upload Interrupted",
                `Uploaded ${successCount} images before an error occurred. The failed images remain on your screen. You can safely click "Upload Images" again to retry them.`,
                "warning"
            );
            setExtractedImages(prev => prev.filter(img => !successfullyUploadedFiles.includes(img.filename)));
        } finally {
            setIsUploading(false);
            setpercentageLoader(false);
        }
    };

    const matchedImages = extractedImages.filter(img => img.selectedItems.length > 0);
    const unmatchedImages = extractedImages.filter(img => img.selectedItems.length === 0);

    const renderImageGrid = (imagesArray, isMatched) => {
        if (imagesArray.length === 0) {
            return (
                <div className="text-center py-5 text-muted bg-light rounded mt-3 border border-dashed">
                    <i className={`fa ${isMatched ? 'fa-check-circle text-success' : 'fa-exclamation-circle text-warning'} fa-3x mb-3`}></i>
                    <h5>{isMatched ? "No mapped images yet." : "All images have been successfully mapped!"}</h5>
                </div>
            );
        }

        return (
            <Row className="mt-4">
                {imagesArray.map((img) => (
                    <Col xs="12" sm="6" md="4" lg="3" key={img.filename} className="mb-4">
                        <div className="border p-3 rounded text-center shadow-sm h-100"
                        >
                            {/* <div className="d-flex justify-content-between align-items-center mb-2">
                                <Badge color={isMatched ? "success" : "warning"} className="px-2 py-1">
                                    {isMatched ? "Mapped" : "Needs Mapping"}
                                </Badge>
                            </div> */}
                            <img
                                src={img.url}
                                alt={img.filename}
                                style={{ width: "100%", height: "160px", objectFit: "contain", backgroundColor: "#f8f9fa" }}
                                className="mb-3 rounded border"
                            />
                            <p className="mb-2 font-weight-bold text-truncate text-muted small" title={img.filename}>
                                <i className="fa fa-file-image-o pr-1"></i> {img.filename}
                            </p>
                            <Select
                                isMulti
                                placeholder="Search & Select Items..."
                                options={itemsList.map(item => ({ value: item.ITEM_ID, label: item.ITEM_DESC }))}
                                value={img.selectedItems}
                                onChange={(options) => handleItemSelect(img.filename, options)}
                                menuPortalTarget={document.body}
                            // styles={{
                            //     menuPortal: base => ({ ...base, zIndex: 9999 }),
                            //     control: base => ({ ...base, borderColor: isMatched ? '#28a745' : '#ffc107' })
                            // }}
                            />
                        </div>
                    </Col>
                ))}
            </Row>
        );
    };

    return (
        <div className="animated fadeIn">
            <div className="O-V-heading-bar d-flex justify-content-between align-items-center mb-3">
                <div className="mt-2">
                    <h4 className="font-weight-bold mb-0">Items Image Upload</h4>
                </div>
                <div className="mt-2">
                    <Link to="/RestMenuOverview" className="btn btn-outline-dark rounded-pill shadow-sm">
                        <i className="fa fa-arrow-left pr-2" /> Back to Overview
                    </Link>
                </div>
            </div>

            <Card className="shadow-sm border-0">
                <CardHeader className="bg-white border-bottom-0 pt-4 pb-0">
                    <h5 className="mb-0">
                        <i className="fa fa-cloud-upload pr-2" aria-hidden="true"></i>
                        Upload ZIP File
                    </h5>
                </CardHeader>
                <CardBody>
                    {isLoadingItems || isProcessingZip || isUploading ? (
                        <div className="text-center py-5">
                            <Responseloader percentage={percentageLoader} />
                        </div>
                    ) : (
                        <>
                            <div className="p-4 bg-light rounded border border-secondary" style={{ borderStyle: 'dashed !important' }}>
                                <label className="font-weight-bold">Select a ZIP file containing item images:</label>
                                <p className="text-muted mb-2">Images named exactly as their Item Descriptions will be auto-matched.</p>
                                <input
                                    type="file"
                                    accept=".zip"
                                    className="form-control-file border p-2 bg-white rounded shadow-sm w-100"
                                    onChange={handleZipUpload}
                                    disabled={isUploading}
                                />
                            </div>

                            {extractedImages.length > 0 && (
                                <div className="mt-5">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">Image Mapping Overview</h5>
                                        {/* <div className="">Total Images: {extractedImages.length}</div> */}
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <Button
                                            hidden={matchedImages.length === 0 && unmatchedImages.length === 0}
                                            color={activeTab === 'matched' ? "outline-danger" : "outline-success"}
                                            onClick={() => setActiveTab(activeTab === 'matched' ? 'unmatched' : 'matched')}
                                            className="font-weight-bold shadow-sm"
                                        >
                                            {activeTab === 'matched' ? (
                                                <>
                                                    <i className="fa fa-exclamation-circle pr-1"></i>
                                                    Show Unmatched Images ({unmatchedImages.length})
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-check-circle pr-1"></i>
                                                    Show Matched Images ({matchedImages.length})
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            color="primary"
                                            // size="lg"
                                            onClick={handleSave}
                                            disabled={isUploading || matchedImages.length === 0}
                                            className="shadow px-4"
                                        >
                                            {isUploading ? (
                                                <><Spinner size="sm" className="mr-2" /> Uploading...</>
                                            ) : (
                                                <><i className="fa fa-upload mr-2" />Upload Images</>
                                            )}
                                        </Button>
                                    </div>

                                    <TabContent activeTab={activeTab} className="p-3 bg-white">
                                        <TabPane tabId="matched">
                                            {activeTab === 'matched' && renderImageGrid(matchedImages, true)}
                                        </TabPane>
                                        <TabPane tabId="unmatched">
                                            <Alert color="warning" className="mt-2 mb-0">
                                                <i className="fa fa-info-circle pr-2"></i>
                                                These images could not be auto-matched. Please map them to an item manually. Once mapped, they will automatically move to the "Matched Images" tab.
                                            </Alert>
                                            {activeTab === 'unmatched' && renderImageGrid(unmatchedImages, false)}
                                        </TabPane>
                                    </TabContent>

                                    {/* <hr className="my-4" />
                                    <div className="">
                                        <Button
                                            color="primary"
                                            size="lg"
                                            onClick={handleSave}
                                            disabled={isUploading || matchedImages.length === 0}
                                            className="shadow px-4"
                                        >
                                            {isUploading ? (
                                                <><Spinner size="sm" className="mr-2" /> Uploading...</>
                                            ) : (
                                                <><i className="fa fa-upload mr-2" />Upload Images</>
                                            )}
                                        </Button>
                                    </div> */}
                                </div>
                            )}
                        </>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default BulkImageUploader;